/**
 * EmailComposeSheet — the ONE shared email "letter" (Jul 2026).
 *
 * The single source of truth for the Ando email compose surface: a continuous
 * bordered sheet (one border + focus-ring, hairline-divided rows) holding
 * recipients → subject → rich body → signature. Reused EVERYWHERE an email is
 * written so every surface gets the SAME functionality (Tiptap toolbar,
 * selection AI-edit, ghost-text autocomplete, recipient autocomplete):
 *   - the inbox REPLY composer (``ReplyComposeSection``),
 *   - the "new email from scratch" / send-email action preview
 *     (``EmailSendDraftPreview`` / ``PecSendPreview``).
 *
 * Fully CONTROLLED — every value + change handler is a prop, so each host wires
 * its own data model (reply local state vs action ``commit(field,…)``) while
 * sharing the exact same UI. The host owns the ``header`` (from-picker or
 * reply-all toggle + "Genera con Ando" + Cc/Ccn reveal + close) and the
 * ``signature`` footer; the sheet owns the recipient / subject / body chrome.
 */

import { msg } from "@lingui/core/macro";
import type * as React from "react";
import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";
import { RecipientChips, type RecipientChipsSuggestion } from "./RecipientChips";
import { type RichEmailAiEdit, RichEmailBody, type RichEmailBodyHandle } from "./RichEmailBody";

/** Contacts autocomplete transport (name / email fragment → hits). */
export type EmailContactsQuery = (q: string) => Promise<readonly RecipientChipsSuggestion[]>;

export interface EmailComposeSheetProps {
  /** Top row — from-picker OR reply-all toggle + Genera + Cc/Ccn reveal + close. */
  readonly header?: React.ReactNode;

  // ── Recipients ────────────────────────────────────────────────────────────
  readonly to: readonly string[];
  readonly onToChange: (next: string[]) => void;
  readonly toError?: string;
  readonly toHelp?: string;
  readonly toLocked?: boolean;
  readonly toPlaceholder?: string;
  readonly toAutoFocus?: boolean;

  readonly cc?: readonly string[];
  readonly onCcChange?: (next: string[]) => void;
  readonly showCc?: boolean;
  readonly ccError?: string;
  readonly ccHelp?: string;
  readonly ccLocked?: boolean;

  readonly bcc?: readonly string[];
  readonly onBccChange?: (next: string[]) => void;
  readonly showBcc?: boolean;
  readonly bccError?: string;
  readonly bccHelp?: string;
  readonly bccLocked?: boolean;

  /** Shared recipient autocomplete (thread participants OR account-scoped People). */
  readonly onQueryContacts?: EmailContactsQuery;

  // ── Subject ───────────────────────────────────────────────────────────────
  readonly subject: string;
  readonly onSubjectChange: (value: string) => void;
  readonly subjectLocked?: boolean;
  readonly subjectPlaceholder?: string;

  // ── Body (rich) ───────────────────────────────────────────────────────────
  /** Controlled HTML body — the Tiptap editor's own markup. */
  readonly bodyHtml: string;
  /** Emits BOTH serialised HTML + plain-text projection on every change. */
  readonly onBodyChange: (html: string, text: string) => void;
  readonly bodyRef?: React.Ref<RichEmailBodyHandle>;
  readonly bodyPlaceholder?: string;
  readonly bodyMinHeight?: number;
  readonly bodyDisabled?: boolean;
  /** ⌘K / selection AI-edit transport. */
  readonly aiEdit?: RichEmailAiEdit;
  /** Ghost-text autocomplete transport. */
  readonly aiComplete?: (input: {
    readonly draft: string;
  }) => Promise<{ readonly suggestion: string }>;
  /**
   * Active Brand Kit fonts — surfaced in the body editor's "Aa" font dropdown
   * above the built-in generics (email-safe fallback chain applied). Absent →
   * only the built-ins. Threaded straight into ``RichEmailBody``.
   */
  readonly brandFonts?: readonly { family: string; fallback: string }[];

  /** Signature (or any trailing content) rendered INSIDE the sheet, below body. */
  readonly signature?: React.ReactNode;
  /**
   * Attachment surface rendered INSIDE the sheet as a hairline-separated band
   * at the bottom (below body + signature, above the host's send bar). Wired
   * by hosts that stage email attachments in the compose surface itself (the
   * inbox reply composer) so every surface shows attachments in the SAME
   * place/style — typically an ``<EmailAttachmentField>``.
   */
  readonly attachments?: React.ReactNode;
  readonly className?: string;
}

/** One bare recipient row + its error / help line + lock overlay. */
const RecipientRow: React.FC<{
  readonly label: string;
  readonly value: readonly string[];
  readonly onChange: (next: string[]) => void;
  readonly onQuery?: EmailContactsQuery;
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
  readonly error?: string;
  readonly help?: string;
  readonly locked?: boolean;
}> = ({ label, value, onChange, onQuery, placeholder, autoFocus, error, help, locked }) => (
  <div
    className={cn(locked && "pointer-events-none opacity-60")}
    aria-disabled={locked || undefined}
  >
    <RecipientChips
      bare
      label={label}
      value={value}
      onChange={onChange}
      onQuery={onQuery}
      placeholder={placeholder}
      autoFocus={autoFocus}
    />
    {error ? (
      <p className="px-3 pb-1.5 text-[11px] text-rose-600">{error}</p>
    ) : help ? (
      <p className="px-3 pb-1.5 text-[11px] text-ink-3">{help}</p>
    ) : null}
  </div>
);
RecipientRow.displayName = "EmailComposeSheet.RecipientRow";

export const EmailComposeSheet: React.FC<EmailComposeSheetProps> = ({
  header,
  to,
  onToChange,
  toError,
  toHelp,
  toLocked,
  toPlaceholder,
  toAutoFocus,
  cc = [],
  onCcChange,
  showCc,
  ccError,
  ccHelp,
  ccLocked,
  bcc = [],
  onBccChange,
  showBcc,
  bccError,
  bccHelp,
  bccLocked,
  onQueryContacts,
  subject,
  onSubjectChange,
  subjectLocked,
  subjectPlaceholder,
  bodyHtml,
  onBodyChange,
  bodyRef,
  bodyPlaceholder,
  bodyMinHeight = 260,
  bodyDisabled,
  aiEdit,
  aiComplete,
  brandFonts,
  signature,
  attachments,
  className,
}) => {
  const { i18n } = useI18n();
  return (
    <div
      className={cn(
        // Focus: il bordo si scalda e il foglio si stacca dalla pagina con
        // un'ombra morbida. Niente anello arancione spesso — su un foglio
        // grande incorniciava tutto invece di dire "stai scrivendo qui".
        "flex flex-col rounded-lg border border-hairline bg-white/60 transition-[border-color,box-shadow] duration-200 focus-within:border-ando-orange/30 focus-within:shadow-[0_2px_16px_-8px_rgba(255,122,26,0.35)]",
        className,
      )}
    >
      {header ? <div className="border-hairline border-b">{header}</div> : null}

      {/* Recipients — bare rows sharing the sheet */}
      <div className="border-hairline border-b">
        <RecipientRow
          label={i18n._(msg`A`)}
          value={to}
          onChange={onToChange}
          onQuery={onQueryContacts}
          placeholder={toPlaceholder ?? i18n._(msg`destinatario@dominio.it`)}
          autoFocus={toAutoFocus}
          error={toError}
          help={toHelp}
          locked={toLocked}
        />
        {showCc && onCcChange ? (
          <RecipientRow
            label={i18n._(msg`Cc`)}
            value={cc}
            onChange={onCcChange}
            onQuery={onQueryContacts}
            placeholder={i18n._(msg`Aggiungi destinatari`)}
            error={ccError}
            help={ccHelp}
            locked={ccLocked}
          />
        ) : null}
        {showBcc && onBccChange ? (
          <RecipientRow
            label={i18n._(msg`Ccn`)}
            value={bcc}
            onChange={onBccChange}
            onQuery={onQueryContacts}
            placeholder={i18n._(msg`Destinatari nascosti`)}
            error={bccError}
            help={bccHelp}
            locked={bccLocked}
          />
        ) : null}
      </div>

      {/* Subject */}
      <input
        type="text"
        value={subject}
        onChange={(e) => onSubjectChange(e.target.value)}
        disabled={subjectLocked}
        aria-label={i18n._(msg`Oggetto`)}
        placeholder={subjectPlaceholder ?? i18n._(msg`Oggetto`)}
        className="w-full border-hairline border-b bg-transparent px-3 py-2 font-medium text-[12.5px] text-ink-2 placeholder:text-ink-3 focus:text-ink-1 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
      />

      {/* Rich body — Tiptap toolbar + selection AI-edit + ghost-text, bare so it
          lives in the sheet; signature rendered inside its footer. */}
      <RichEmailBody
        ref={bodyRef}
        bare
        toolbar
        valueHtml={bodyHtml}
        onChange={onBodyChange}
        placeholder={bodyPlaceholder ?? i18n._(msg`Scrivi il messaggio…`)}
        minHeight={bodyMinHeight}
        maxHeight={100_000}
        disabled={bodyDisabled}
        aiEdit={aiEdit}
        aiComplete={aiComplete}
        brandFonts={brandFonts}
        footer={signature}
      />

      {/* Attachments — a hairline-separated band at the bottom of the sheet, so
          every compose surface shows staged attachments in the SAME place +
          pencil style (chips) just above the host's send bar. */}
      {attachments ? (
        <div className="border-hairline border-t px-3 py-2.5">{attachments}</div>
      ) : null}
    </div>
  );
};
EmailComposeSheet.displayName = "EmailComposeSheet";
